import { FC, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { ImageDropzoneProps, DropzoneFile, FileError } from "./Dropzone.model";

const Dropzone: FC<ImageDropzoneProps> = ({
  onFilesAccepted,
  openFileDialog,
  allowMultiple = false,
  maxFileSize = 10,
  maxFiles = 5,
  acceptTypes = [
    { type: "image/png", label: "PNG" },
    { type: "image/jpeg", label: "JPEG" },
  ],
  activeLinkText = "Dodaj zdjęcie / zdjęcia z dysku",
  showInvalidFiles = false,
}) => {
  const translateFileError = (error: FileError, maxFiles: number): string => {
    switch (error.code) {
      case "file-invalid-type":
        return __("Nieprawidłowy format");
      case "file-too-large":
        return __("Plik jest za za duży");
      case "too-many-files":
        return __(`Można dodać maksymalnie ${maxFiles} plików`);
      default:
        return error.message;
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    fileRejections: rejectedFiles,
  } = useDropzone({
    maxFiles,
    accept: acceptTypes.reduce((acc, { type }) => ({ ...acc, [type]: [] }), {}),
    maxSize: maxFileSize * 1024 * 1024,
    multiple: allowMultiple,
    onDrop: (acceptedFiles, fileRejections) => {
      const files: DropzoneFile[] = [];

      acceptedFiles.forEach((file) => {
        files.push({
          file,
          isValid: true,
        });
      });

      fileRejections.forEach(({ file, errors }) => {
        const translatedErrors = errors
          .map((err) => translateFileError(err, maxFiles))
          .join(", ");

        if (showInvalidFiles) {
          files.push({
            file,
            isValid: false,
            errorMessage: translatedErrors,
          });
        }
      });

      onFilesAccepted(files);
    },
  });

  const fileRejectionItems = rejectedFiles.map(({ file, errors }) => (
    <Box key={file.path}>
      {file.name}:
      <Box component="ul">
        {errors.map((_e) => (
          <Box
            component="li"
            sx={{ listStylePosition: "inside" }}
            key={_e.code}
          >
            {translateFileError(_e, maxFiles)}
          </Box>
        ))}
      </Box>
    </Box>
  ));

  useEffect(() => {
    if (openFileDialog) {
      open();
    }
  }, [openFileDialog, open]);

  useEffect(() => {
    if (fileRejectionItems.length > 0 && !showInvalidFiles) {
      const messages = rejectedFiles
        .map(
          ({ file, errors }) =>
            `${file.name}: ${errors
              .map((_e) => translateFileError(_e, maxFiles))
              .join(", ")}`
        )
        .join("\u000A");
      dynamicNotification(messages, "error", "top-right", 7000);
    }
  }, [fileRejectionItems.length, maxFiles, rejectedFiles, showInvalidFiles]);

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #0000001F",
          borderRadius: "8px",
          padding: "16px",
          textAlign: "center",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} />
        <UploadFileOutlinedIcon sx={{ margin: "8px 0" }} color="primary" />
        <Typography
          variant="body1"
          sx={{
            marginBottom: "8px",
          }}
        >
          {isDragActive ? (
            `${__("Upuść plik aby go załadować")}:`
          ) : (
            <>
              <Box
                component="span"
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                {__(activeLinkText)}
              </Box>{" "}
              {__("lub przeciągnij tutaj")}
            </>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${acceptTypes
            .map(({ label }) => label)
            .join(", ")} (max. ${maxFileSize}MB)`}
        </Typography>
      </Box>
    </>
  );
};

export default Dropzone;
